import { useCallback, useEffect, useRef, useState } from 'react';
import AgentModelChip from '../common/AgentModelChip';
import { AgentErrorBanner } from '../common/AgentErrorBanner';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useWebSocket } from '../../contexts/WebSocketContext';
import api from '../../services/api';
import type { NewsBeatChat, NewsBeatSurface } from '../../types';

/**
 * The beat agent, as a right-hand slideout.
 *
 * One conversation per beat, opened at one of two surfaces. On Setup it tunes:
 * reads the settings and what the last run did with them, changes them, runs
 * the beat, reads the result. On a briefing it combs: merge, split, retag,
 * un-mute, for tidying a report before acting on it. The tool set is chosen
 * server-side from `surface` — this component only says which one it wants.
 *
 * THE RETURN LEG. A tuning round ends with a run queued and minutes of silence.
 * When the agent starts a run it tells us which briefing to watch
 * (`started_briefing_id`), and when that run finishes we send the evaluation
 * message ourselves. That is what makes it a loop rather than a chat where the
 * user has to remember to come back and ask.
 */

const STARTERS: Record<NewsBeatSurface, string[]> = {
  tune: [
    'Is this beat set up right?',
    'Why did the last run come back so thin?',
    'What would you change about the filters?',
  ],
  comb: [
    'Why was an article dropped?',
    'What did you hold back as already reported?',
    'Merge any stories that are really the same thing',
  ],
};

const TITLE: Record<NewsBeatSurface, string> = {
  tune: 'Tune this beat',
  comb: 'Ask AI',
};

export default function BeatAgentPane({
  beatId,
  surface,
  briefingId,
  open,
  onClose,
  onChanged,
  onSetupChanged,
  seed,
  onSeedConsumed,
}: {
  beatId: number;
  surface: NewsBeatSurface;
  briefingId?: number | null;
  open: boolean;
  onClose: () => void;
  /** A briefing was edited — the reading UI should refetch. */
  onChanged: () => void;
  /** A SETTING was written — the Setup form has to reconcile. */
  onSetupChanged?: () => void;
  /** Text to send on open, from a verdict chip's "ask about this". */
  seed?: string | null;
  onSeedConsumed?: () => void;
}) {
  const { subscribe } = useWebSocket();
  const [chat, setChat] = useState<NewsBeatChat | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement | null>(null);
  // The run this pane started and is waiting on, so the finish event can be
  // told apart from any other run of this beat (the scheduler fires too).
  const watching = useRef<number | null>(null);

  useEscapeKey(open, onClose);

  const load = useCallback(async () => {
    try {
      setChat(await api.getNewsBeatChat(beatId, surface));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [beatId, surface]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content) return;
      setDraft('');
      setError(null);
      try {
        setChat(
          await api.sendNewsBeatChatMessage(beatId, content, surface, briefingId),
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [beatId, surface, briefingId],
  );

  // A verdict chip can hand us an opening line.
  useEffect(() => {
    if (!open || !seed) return;
    send(seed);
    onSeedConsumed?.();
  }, [open, seed, send, onSeedConsumed]);

  useEffect(() => {
    return subscribe((event: any) => {
      if (event?.beat_id !== beatId) return;

      if (event.event_type === 'news_beat_chat') {
        load();
        if (event.status === 'replied') {
          onChanged();
          if (event.setup_changed) onSetupChanged?.();
          if (event.started_briefing_id) {
            watching.current = event.started_briefing_id;
          }
        }
        return;
      }

      // The run the agent started has landed. Bring it back to the agent —
      // this is the evaluate half of the loop, and it runs exactly once per
      // run so it can't turn into an unattended cycle.
      if (
        event.event_type === 'news_beat' &&
        watching.current &&
        event.briefing_id === watching.current &&
        ['finished', 'failed', 'cancelled'].includes(event.status)
      ) {
        const finished = watching.current;
        watching.current = null;
        onChanged();
        send(
          `Run ${finished} has finished. Read the new briefing and tell me ` +
            `whether the change worked - call get_run_verdict and compare_runs ` +
            `first. Don't change anything else yet.`,
        );
      }
    });
  }, [subscribe, beatId, load, onChanged, onSetupChanged, send]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat?.messages.length, chat?.status]);

  const thinking = chat?.status === 'thinking';
  const visible = (chat?.messages ?? []).filter((m) => m.role !== 'system');
  const starters = STARTERS[surface];

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden
      />
      {/* `inset-y-0` rather than `top-0 h-full`: the height is then DERIVED from
          the viewport instead of being a percentage of it, so the panel cannot
          end up a few pixels taller than the window and push its composer off
          the bottom edge. */}
      <aside
        role="dialog"
        aria-label={TITLE[surface]}
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white dark:bg-gray-800 shadow-xl flex flex-col transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {TITLE[surface]}
          </span>
          <div className="flex items-center gap-2">
            <AgentModelChip stage="NEWS_BEAT_CHAT_AGENT" />
            <button
              onClick={onClose}
              aria-label="Close"
              className="icon-btn text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              ×
            </button>
          </div>
        </div>

        {/* min-h-0 is load-bearing. A flex child's automatic minimum size is its
            CONTENT size, so without it this pane refuses to shrink once the
            transcript is taller than the column — it grows the flex container
            instead and shoves the composer past the bottom of the screen. The
            scrollbar here is the point; it can only appear if this may shrink. */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
          {visible.length === 0 && (
            <div className="space-y-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {surface === 'tune'
                  ? 'I can read what the last run did and change the settings. Tell me what the briefing should look like.'
                  : 'Ask about this briefing, or tell me what to fix.'}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {starters.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-xs px-2 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-indigo-400"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {visible.map((m) => (
            <div
              key={m.id}
              className={`text-sm whitespace-pre-line rounded-lg px-3 py-2 ${
                m.role === 'user'
                  ? 'bg-indigo-50 dark:bg-indigo-900/20 text-gray-900 dark:text-gray-100 ml-6'
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 mr-6'
              }`}
            >
              {m.content}
            </div>
          ))}

          {thinking && (
            <div className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
              Thinking…
            </div>
          )}
          {!thinking && watching.current && (
            <div className="text-xs text-blue-600 dark:text-blue-400">
              Waiting for run {watching.current} to finish - I'll read it when it
              lands.
            </div>
          )}
          <div ref={bottom} />
        </div>

        <AgentErrorBanner
          error={error ?? chat?.last_error}
          className="shrink-0 border-b-0 border-t border-gray-200 dark:border-gray-700"
        />

        {(chat?.suggested_prompts?.length ?? 0) > 0 && !thinking && (
          <div className="shrink-0 px-3 pt-2 flex flex-wrap gap-1.5">
            {chat!.suggested_prompts.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-xs px-2 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-indigo-400"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="shrink-0 p-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send(draft)}
            disabled={thinking}
            placeholder={
              thinking
                ? 'Waiting for a reply…'
                : surface === 'tune'
                  ? 'What should this beat catch?'
                  : 'Ask about this briefing…'
            }
            className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm"
          />
          <button
            onClick={() => send(draft)}
            disabled={thinking || !draft.trim()}
            className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </aside>
    </>
  );
}
